import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaTimes,
  FaEdit,
  FaTrash,
  FaRupeeSign,
  FaClock,
  FaUtensils,
  FaTag,
  FaUndo,
  FaCheckCircle,
  FaTimesCircle,
  FaHamburger,
  FaFilter,
  FaPlus,
  FaBarcode,
  FaLeaf,
  FaDrumstickBite,
  FaImage,
} from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function MenuItemsManagment() {
  const navigate = useNavigate();
  const [foodItems, setFoodItems] = useState([]);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [editingFoodItem, setEditingFoodItem] = useState(null);
  const [deleteFoodId, setDeleteFoodId] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTimePicker, setShowTimePicker] = useState({
    availableFrom: false,
    availableUntil: false,
  });
  const [tempTime, setTempTime] = useState({
    availableFrom: '',
    availableUntil: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  const [newFoodItem, setNewFoodItem] = useState({
    name: '',
    description: '',
    price: '',
    price_type: 'Per person',
    image: null,
    foodType: 'Veg',
    subCategoryId: '',
    mealTypes: [],
    availableFrom: '',
    availableUntil: '',
    status: 'Available',
    showEnhanceStay: false,
    refundable: false,
    value: '',
    value_type: '',
    showInCheckin: false,
    itemcode: '',
  });

  // Fetch food items, subcategories, and categories from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await fetchFoodItems();
        await fetchSubCategories();
        await fetchCategories();
      } catch (error) {
        toast.error('Failed to fetch data.');
        console.error('Fetch error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // ✅ Updated formatTime function
  const formatTime = (timeStr) => {
    if (!timeStr) return null;

    // If it's already in HH:MM:SS format, return as is
    if (timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return timeStr;
    }

    // If it's in HH:MM format, add :00 seconds
    if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
      const parts = timeStr.split(':');
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1];
      return `${hours}:${minutes}:00`;
    }

    return null;
  };

  // Fetch food items with search and filter support
  const fetchFoodItems = async (search = '', filter = '') => {
    try {
      let url = `${BQ_BASE_URL}/bq/api/items/`;

      const params = new URLSearchParams();
      if (search) {
        if (!isNaN(search) && search.trim() !== '') {
          params.append('itemcode', search);
        } else {
          params.append('search', search);
        }
      }
      if (filter && filter !== 'all') {
        const filterMap = {
          available: 'isActive=true',
          'not-available': 'isActive=false',
          'enhance-stay': 'showEnhanceStay=true',
          refundable: 'refundable=true',
          veg: 'foodType=Veg',
          'non-veg': 'foodType=Non-Veg',
        };
        if (filterMap[filter]) {
          const [key, value] = filterMap[filter].split('=');
          params.append(key, value);
        }
      }

      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        const mappedData = data.items.map((item) => ({
          id: item.id,
          name: item.title,
          description: item.description,
          price: item.price,
          price_type: item.price_type || 'Per person',
          image: item.image,
          status: item.isActive ? 'Available' : 'Not Available',
          foodType: item.foodType || 'Veg',
          mealTypes: item.mealTypes || ['Breakfast'],
          availableFrom: item.availableFrom || '',
          availableUntil: item.availableUntil || '',
          subCategoryId: item.subCategoryId || '',
          showEnhanceStay: item.showEnhanceStay || false,
          refundable: item.refundable || false,
          value: item.value || 0,
          value_type: item.value_type || '',
          showInCheckin: item.showInCheckin || false,
          itemcode: item.itemcode || 0,
          createdDate: item.createdDate || '',
          updatedDate: item.updatedDate || '',
        }));
        setFoodItems(mappedData);
        return mappedData;
      } else {
        toast.error(data.message || 'Failed to fetch food items.');
        return [];
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('An error occurred while fetching food items.');
      return [];
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/categories/`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      if (response.ok) {
        setCategories(data);
        return data;
      } else {
        console.error('Failed to fetch categories');
        return [];
      }
    } catch (error) {
      console.error('Fetch categories error:', error);
      return [];
    }
  };

  // Fetch subcategories
  const fetchSubCategories = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/subcategories/`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      if (response.ok) {
        setSubCategories(data);
        return data;
      } else {
        toast.error(data.message || 'Failed to fetch subcategories.');
        return [];
      }
    } catch (error) {
      console.error('Fetch subcategories error:', error);
      toast.error('An error occurred while fetching subcategories.');
      return [];
    }
  };

  // Get category ID from subcategory ID
  const getCategoryIdFromSubCategoryId = (subCategoryId) => {
    const subcategory = subCategories.find((sub) => sub.id === subCategoryId);
    return subcategory ? subcategory.categoryid : null;
  };

  // Filter items based on selected category and subcategory
  const getFilteredItems = () => {
    let filtered = foodItems.filter((item) => {
      const searchLower = searchQuery.toLowerCase();
      const nameMatch = item.name.toLowerCase().includes(searchLower);
      const itemcodeMatch =
        !isNaN(searchQuery) && searchQuery.trim() !== ''
          ? item.itemcode === parseInt(searchQuery)
          : false;
      return nameMatch || itemcodeMatch;
    });

    if (selectedCategory) {
      filtered = filtered.filter((item) => {
        const categoryId = getCategoryIdFromSubCategoryId(item.subCategoryId);
        return categoryId === selectedCategory;
      });
    }

    if (selectedSubcategory) {
      filtered = filtered.filter((item) => item.subCategoryId === selectedSubcategory);
    }

    switch (activeFilter) {
      case 'available':
        filtered = filtered.filter((item) => item.status === 'Available');
        break;
      case 'not-available':
        filtered = filtered.filter((item) => item.status === 'Not Available');
        break;
      case 'enhance-stay':
        filtered = filtered.filter((item) => item.showEnhanceStay);
        break;
      case 'refundable':
        filtered = filtered.filter((item) => item.refundable);
        break;
      case 'veg':
        filtered = filtered.filter((item) => item.foodType === 'Veg');
        break;
      case 'non-veg':
        filtered = filtered.filter((item) => item.foodType === 'Non-Veg');
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredFoodItems = getFilteredItems();

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setActiveFilter('all');
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    fetchFoodItems('', 'all');
  };

  // Add new food item (POST)
  const handleAddFoodItem = async () => {
    if (
      !newFoodItem.name ||
      !newFoodItem.description ||
      !newFoodItem.price ||
      !newFoodItem.subCategoryId ||
      !newFoodItem.mealTypes.length ||
      !newFoodItem.foodType
    ) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', newFoodItem.name);
      formData.append('description', newFoodItem.description);
      formData.append('price', parseFloat(newFoodItem.price));
      formData.append('price_type', newFoodItem.price_type);
      formData.append('foodType', newFoodItem.foodType);
      formData.append('subCategoryId', newFoodItem.subCategoryId);
      formData.append('mealTypes', JSON.stringify(newFoodItem.mealTypes));

      // ✅ Use the updated formatTime function
      const fromTime = formatTime(newFoodItem.availableFrom);
      const untilTime = formatTime(newFoodItem.availableUntil);

      // Only append if not null
      if (fromTime) {
        formData.append('availableFrom', fromTime);
      }
      if (untilTime) {
        formData.append('availableUntil', untilTime);
      }

      formData.append('isActive', newFoodItem.status === 'Available');
      formData.append('showEnhanceStay', newFoodItem.showEnhanceStay);
      formData.append('refundable', newFoodItem.refundable);
      formData.append('value', newFoodItem.value || 0);
      formData.append('value_type', newFoodItem.value_type || '');
      formData.append('showInCheckin', newFoodItem.showInCheckin || false);
      formData.append('itemcode', parseInt(newFoodItem.itemcode) || 0);

      if (newFoodItem.image) {
        formData.append('image', newFoodItem.image);
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/items/`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        await fetchFoodItems(searchQuery, activeFilter);
        setShowAddPopup(false);
        resetNewFoodItem();
        toast.success('Food item added successfully!');
      } else {
        toast.error(data.message || 'Failed to add food item.');
      }
    } catch (error) {
      toast.error('An error occurred while adding the food item.');
      console.error('Add error:', error);
    }
  };

  // Edit food item (PUT)
  const handleEditFoodItem = async () => {
    if (
      !editingFoodItem.name ||
      !editingFoodItem.description ||
      !editingFoodItem.price ||
      !editingFoodItem.subCategoryId ||
      !editingFoodItem.mealTypes.length ||
      !editingFoodItem.foodType
    ) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', editingFoodItem.name);
      formData.append('description', editingFoodItem.description);
      formData.append('price', parseFloat(editingFoodItem.price));
      formData.append('price_type', editingFoodItem.price_type);
      formData.append('foodType', editingFoodItem.foodType);
      formData.append('subCategoryId', editingFoodItem.subCategoryId);
      formData.append('mealTypes', JSON.stringify(editingFoodItem.mealTypes));

      // ✅ Use the updated formatTime function
      const fromTime = formatTime(editingFoodItem.availableFrom);
      const untilTime = formatTime(editingFoodItem.availableUntil);

      if (fromTime) {
        formData.append('availableFrom', fromTime);
      }
      if (untilTime) {
        formData.append('availableUntil', untilTime);
      }

      formData.append('isActive', editingFoodItem.status === 'Available');
      formData.append('showEnhanceStay', editingFoodItem.showEnhanceStay);
      formData.append('refundable', editingFoodItem.refundable);
      formData.append('value', editingFoodItem.value || 0);
      formData.append('value_type', editingFoodItem.value_type || '');
      formData.append('showInCheckin', editingFoodItem.showInCheckin || false);
      formData.append('itemcode', parseInt(editingFoodItem.itemcode) || 0);

      if (editingFoodItem.image) {
        if (typeof editingFoodItem.image === 'string' && editingFoodItem.image.startsWith('http')) {
          formData.append('image_url', editingFoodItem.image);
        } else {
          formData.append('image', editingFoodItem.image);
        }
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/items/${editingFoodItem.id}`, {
        method: 'PUT',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        await fetchFoodItems(searchQuery, activeFilter);
        setShowEditPopup(false);
        toast.success('Food item updated successfully!');
      } else {
        toast.error(data.message || 'Failed to update food item.');
      }
    } catch (error) {
      toast.error('An error occurred while updating the food item.');
      console.error('Update error:', error);
    }
  };

  // Delete food item (DELETE)
  const handleDeleteFoodItem = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/items/${deleteFoodId}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        await fetchFoodItems(searchQuery, activeFilter);
        setShowDeletePopup(false);
        toast.success(data.message || 'Food item deleted successfully!');
      } else {
        toast.error(data.message || 'Failed to delete food item.');
      }
    } catch (error) {
      toast.error('An error occurred while deleting the food item.');
      console.error('Delete error:', error);
    }
  };

  // Reset new food item form
  const resetNewFoodItem = () => {
    setNewFoodItem({
      name: '',
      description: '',
      price: '',
      price_type: 'Per person',
      image: null,
      foodType: 'Veg',
      subCategoryId: '',
      mealTypes: [],
      availableFrom: '',
      availableUntil: '',
      status: 'Available',
      showEnhanceStay: false,
      refundable: false,
      value: '',
      value_type: '',
      showInCheckin: false,
      itemcode: '',
    });
  };

  // Handle search with API
  const handleSearch = async (query) => {
    setSearchQuery(query);
    await fetchFoodItems(query, activeFilter);
  };

  // Handle filter change with API
  const handleFilterChange = async (filter) => {
    setActiveFilter(filter);
    await fetchFoodItems(searchQuery, filter);
  };

  // Handle time selection with custom picker
  const handleTimeFocus = (field) => {
    setShowTimePicker({
      ...showTimePicker,
      [field]: true,
    });
    setTempTime({
      ...tempTime,
      [field]: editingFoodItem ? editingFoodItem[field] : '12:00',
    });
  };

  const handleTimeChange = (field, value) => {
    setTempTime({
      ...tempTime,
      [field]: value,
    });
  };

  // ✅ Updated confirmTimeSelection with proper time formatting
  const confirmTimeSelection = (field, time) => {
    // Ensure time is in HH:MM format with leading zeros
    let formattedTime = time;
    if (time && time.includes(':')) {
      const parts = time.split(':');
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1] ? parts[1].padStart(2, '0') : '00';
      formattedTime = `${hours}:${minutes}`;
    }

    if (editingFoodItem) {
      setEditingFoodItem({
        ...editingFoodItem,
        [field]: formattedTime,
      });
    } else {
      setNewFoodItem({
        ...newFoodItem,
        [field]: formattedTime,
      });
    }
    setShowTimePicker({
      ...showTimePicker,
      [field]: false,
    });
  };

  const cancelTimeSelection = (field) => {
    setShowTimePicker({
      ...showTimePicker,
      [field]: false,
    });
  };

  // Custom time picker component
  const TimePickerModal = ({ field, onConfirm, onCancel }) => {
    const [selectedTime, setSelectedTime] = useState(tempTime[field] || '12:00');

    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

    const formatTime = (hour, minute) => {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour.toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')} ${period}`;
    };

    const handleHourChange = (hour) => {
      const [_, minute] = selectedTime.split(':');
      const newTime = `${hour}:${minute || '00'}`;
      setSelectedTime(newTime);
      handleTimeChange(field, newTime);
    };

    const handleMinuteChange = (minute) => {
      const [hour] = selectedTime.split(':');
      const newTime = `${hour}:${minute}`;
      setSelectedTime(newTime);
      handleTimeChange(field, newTime);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-sm sm:max-w-md relative">
          <button
            onClick={onCancel}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <FaTimes className="w-5 h-5" />
          </button>

          <h3 className="text-lg font-semibold mb-4 pr-6">
            Select {field === 'availableFrom' ? 'Available From' : 'Available Until'} Time
          </h3>

          <div className="flex flex-wrap justify-between gap-4 mb-6">
            <div className="flex-1 min-w-[100px]">
              <label className="block text-sm font-medium mb-2">Hour</label>
              <select
                value={selectedTime.split(':')[0]}
                onChange={(e) => handleHourChange(e.target.value)}
                className="p-2 border rounded w-full"
              >
                {hours.map((hour) => (
                  <option key={hour} value={hour}>
                    {hour % 12 || 12}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[100px]">
              <label className="block text-sm font-medium mb-2">Minute</label>
              <select
                value={selectedTime.split(':')[1]}
                onChange={(e) => handleMinuteChange(e.target.value)}
                className="p-2 border rounded w-full"
              >
                {minutes.map((minute) => (
                  <option key={minute} value={minute.toString().padStart(2, '0')}>
                    {minute.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[100px]">
              <label className="block text-sm font-medium mb-2">AM/PM</label>
              <select
                value={parseInt(selectedTime.split(':')[0]) >= 12 ? 'PM' : 'AM'}
                onChange={(e) => {
                  const [hour, minute] = selectedTime.split(':');
                  const newHour =
                    e.target.value === 'PM' ? (parseInt(hour) % 12) + 12 : parseInt(hour) % 12;
                  handleHourChange(newHour);
                }}
                className="p-2 border rounded w-full"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>

          <div className="text-center mb-4">
            <div className="text-xl sm:text-2xl font-bold bg-gray-100 p-3 rounded">
              {formatTime(
                parseInt(selectedTime.split(':')[0]),
                parseInt(selectedTime.split(':')[1])
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => onConfirm(selectedTime)}
              className="w-full px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Mobile filters dropdown
  const FilterDropdown = () => (
    <div className="relative">
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        <FaFilter className="text-gray-600" />
        <span className="hidden sm:inline">Filter</span>
      </button>

      {showFilters && (
        <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
          <div className="p-2">
            {[
              { value: 'all', label: 'All Items' },
              { value: 'available', label: 'Available' },
              { value: 'not-available', label: 'Not Available' },
              { value: 'enhance-stay', label: 'Enhance Stay' },
              { value: 'refundable', label: 'Refundable' },
              { value: 'veg', label: 'Veg Only' },
              { value: 'non-veg', label: 'Non-Veg Only' },
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => {
                  handleFilterChange(filter.value);
                  setShowFilters(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md mb-1 last:mb-0 ${
                  activeFilter === filter.value ? 'bg-black text-white' : 'hover:bg-gray-100'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
      {[...Array(8)].map((_, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg overflow-hidden shadow animate-pulse"
        >
          <div className="h-48 bg-gray-300"></div>
          <div className="p-4 space-y-3">
            <div className="h-6 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="flex gap-2">
              <div className="h-6 w-16 bg-gray-300 rounded"></div>
              <div className="h-6 w-16 bg-gray-300 rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {/* Header Navigation */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Menu Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/menu-items-managment')}
            className="btn text-black font-bold"
          >
            Menu Items Management
          </button>
          <button onClick={() => navigate('/dashboard/mdm/menu-Categorization')} className="btn ">
            Menu Categorization
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/menu-pricing-promotions')}
            className="btn "
          >
            Menu Pricing & Promotions
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/menu-analytics-insights')}
            className="btn "
          >
            Menu Analytics & Insights
          </button>
        </div>
      </header>

      <h1 className="text-2xl font-bold text-center">Menu Management</h1>
      <p className="text-sm text-center mb-6">
        Menu Management allows admins to create food categories, subcategories, and individual food
        items for organized menu structuring and updates.
      </p>

      {/* Search and Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-2">
        <div className="relative flex-1 w-full">
          <input
            type="text"
            placeholder="Search by name or item code (e.g., 'mango' or '4')..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm outline-none"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>

        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <FilterDropdown />

          <div className="hidden sm:flex gap-2">
            {[
              { value: 'all', label: 'All' },
              { value: 'available', label: 'Available' },
              { value: 'refundable', label: 'Refundable' },
              { value: 'enhance-stay', label: 'Enhance Stay' },
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => handleFilterChange(filter.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === filter.value
                    ? 'bg-black text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {(selectedCategory || selectedSubcategory || searchQuery || activeFilter !== 'all') && (
            <button
              onClick={clearAllFilters}
              className="px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <FaTimes size={12} />
              Clear Filters
            </button>
          )}

          <button
            onClick={() => {
              resetNewFoodItem();
              setShowAddPopup(true);
            }}
            className="flex-1 sm:flex-none px-4 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <FaPlus />
            Add Food Item
          </button>
        </div>
      </div>

      {/* Food Items Count */}
      <div className="mb-2 flex justify-between items-center">
        <div>
          <span className="text-gray-600">
            Showing {filteredFoodItems.length} of {foodItems.length} menu items
          </span>
          {selectedCategory && (
            <span className="ml-2 text-sm text-gray-500">
              in {categories.find((c) => c.id === selectedCategory)?.name}
            </span>
          )}
          {selectedSubcategory && (
            <span className="ml-2 text-sm text-gray-500">
              › {subCategories.find((s) => s.id === selectedSubcategory)?.name}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500">
          {activeFilter !== 'all' && (
            <button
              onClick={() => handleFilterChange('all')}
              className="text-blue-600 hover:underline"
            >
              Clear status filter
            </button>
          )}
        </div>
      </div>

      {/* Category Filters */}
      <div className="overflow-x-auto mb-3 pb-2">
        <div className="flex gap-2 min-w-min">
          <button
            onClick={() => {
              setSelectedCategory(null);
              setSelectedSubcategory(null);
            }}
            className={`px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
              selectedCategory === null
                ? 'bg-black text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
            }`}
          >
            All Categories
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => {
                setSelectedCategory(category.id);
                setSelectedSubcategory(null);
              }}
              className={`px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                selectedCategory === category.id
                  ? 'bg-black text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Subcategory Filters */}
      {selectedCategory &&
        subCategories.filter((s) => s.categoryid === selectedCategory).length > 0 && (
          <div className="overflow-x-auto mb-6 pb-2">
            <div className="flex gap-2 min-w-min">
              <button
                onClick={() => setSelectedSubcategory(null)}
                className={`px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
                  selectedSubcategory === null
                    ? 'bg-black text-white'
                    : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
                }`}
              >
                All Subcategories
              </button>
              {subCategories
                .filter((sub) => sub.categoryid === selectedCategory)
                .map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => setSelectedSubcategory(sub.id)}
                    className={`px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
                      selectedSubcategory === sub.id
                        ? 'bg-black text-white'
                        : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
            </div>
          </div>
        )}

      {/* Food Cards Grid */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : filteredFoodItems.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">
            <FaUtensils className="mx-auto" />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No food items found</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || selectedCategory || selectedSubcategory
              ? 'Try adjusting your filters or search terms'
              : 'Start by adding your first food item'}
          </p>
          {!searchQuery && !selectedCategory && !selectedSubcategory && (
            <button
              onClick={() => {
                resetNewFoodItem();
                setShowAddPopup(true);
              }}
              className="px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
            >
              Add First Food Item
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
          {filteredFoodItems.map((item) => {
            const subCategory = subCategories.find((s) => s.id === item.subCategoryId);
            const category = subCategory
              ? categories.find((c) => c.id === subCategory.categoryid)
              : null;

            return (
              <div
                key={item.id}
                className="group border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
              >
                {/* Image with badges */}
                <div className="relative h-48 sm:h-56 overflow-hidden bg-gray-100">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = '';
                        e.target.onerror = null;
                        e.target.className = 'hidden';
                        e.target.parentElement.querySelector(
                          '.no-image-placeholder'
                        ).style.display = 'flex';
                      }}
                    />
                  ) : null}
                  {/* ✅ No Image Placeholder */}
                  <div
                    className="no-image-placeholder w-full h-full flex flex-col items-center justify-center text-gray-400"
                    style={{ display: item.image ? 'none' : 'flex' }}
                  >
                    <FaImage className="text-5xl mb-2" />
                    <span className="text-sm">No Image</span>
                  </div>

                  {/* Top badges */}
                  <div className="absolute top-3 right-3 flex flex-col gap-2">
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        item.status === 'Available'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {item.status}
                    </span>

                    {item.refundable && (
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <FaUndo className="text-xs" />
                        Refundable
                      </span>
                    )}
                  </div>

                  {/* Bottom badges */}
                  <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
                    {item.showEnhanceStay && (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <FaTag />
                        Enhance Stay
                      </span>
                    )}

                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        item.foodType === 'Veg'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {item.foodType === 'Veg' ? (
                        <FaLeaf className="inline mr-1" />
                      ) : (
                        <FaDrumstickBite className="inline mr-1" />
                      )}
                      {item.foodType}
                    </span>

                    {item.itemcode > 0 && (
                      <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <FaBarcode className="text-xs" />
                        Code: {item.itemcode}
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 flex-grow flex flex-col">
                  {/* Name and Price */}
                  <div className="mb-3">
                    <h2 className="text-lg font-bold text-gray-900 line-clamp-1 mb-1">
                      {item.name}
                    </h2>
                    <div className="flex items-center ">
                      <div className="flex items-center gap-2">
                        <FaRupeeSign className="text-gray-700" />
                        <span className="text-xl font-bold text-gray-900">{item.price} </span>
                      </div>
                      <span className="text-sm text-gray-600">/{item.price_type}</span>
                    </div>
                    {category && (
                      <div className="text-xs text-gray-500 mt-1">
                        {category.name} {subCategory && `› ${subCategory.name}`}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-grow">
                    {item.description}
                  </p>

                  {/* Meal Types */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {item.mealTypes.map((type) => (
                        <span
                          key={type}
                          className="bg-gray-100 text-gray-800 text-xs py-1 px-2 rounded flex items-center gap-1"
                        >
                          <FaUtensils className="text-xs" />
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Availability Times */}
                  {(item.availableFrom || item.availableUntil) && (
                    <div className="mb-4 p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <FaClock />
                        <span className="font-medium">Available</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        {item.availableFrom ? (
                          <span className="text-gray-700">
                            From: {item.availableFrom.substring(0, 5)}
                          </span>
                        ) : (
                          <span className="text-gray-400">No start time</span>
                        )}
                        {item.availableUntil ? (
                          <span className="text-gray-700">
                            Until: {item.availableUntil.substring(0, 5)}
                          </span>
                        ) : (
                          <span className="text-gray-400">No end time</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Info */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    {item.showInCheckin && (
                      <span className="flex items-center gap-1">
                        <FaCheckCircle className="text-green-500" />
                        Shows in Check-in
                      </span>
                    )}
                    {item.value > 0 && <span>Tax : {item.value}%</span>}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setEditingFoodItem({
                          ...item,
                          subCategoryId: item.subCategoryId || '',
                          image: item.image || '',
                        });
                        setShowEditPopup(true);
                      }}
                      className="flex-1 px-3 py-2 bg-gray-900 hover:bg-black text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <FaEdit />
                      <span className="hidden xs:inline">Edit</span>
                    </button>

                    <button
                      onClick={() => {
                        setDeleteFoodId(item.id);
                        setShowDeletePopup(true);
                      }}
                      className="flex-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <FaTrash />
                      <span className="hidden xs:inline">Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add Food Item Popup */}
      {showAddPopup && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Add New Food Item</h2>
              <button
                onClick={() => setShowAddPopup(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Food Name *</label>
                    <input
                      type="text"
                      value={newFoodItem.name}
                      onChange={(e) =>
                        setNewFoodItem({
                          ...newFoodItem,
                          name: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter food name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Description *</label>
                    <textarea
                      value={newFoodItem.description}
                      onChange={(e) =>
                        setNewFoodItem({
                          ...newFoodItem,
                          description: e.target.value,
                        })
                      }
                      rows="3"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Price *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newFoodItem.price}
                      onChange={(e) =>
                        setNewFoodItem({
                          ...newFoodItem,
                          price: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter price"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Price Type</label>
                    <select
                      value={newFoodItem.price_type}
                      onChange={(e) =>
                        setNewFoodItem({
                          ...newFoodItem,
                          price_type: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="Per item">Per Item</option>
                      <option value="Per person">Per Person</option>
                      <option value="Per plate">Per Plate</option>
                      <option value="Per pack">Per Pack</option>
                      <option value="Per day">Per Day</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Sub-Category *</label>
                    <select
                      value={newFoodItem.subCategoryId}
                      onChange={(e) =>
                        setNewFoodItem({
                          ...newFoodItem,
                          subCategoryId: e.target.value,
                        })
                      }
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                        !newFoodItem.subCategoryId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Sub-Category</option>
                      {subCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      <FaBarcode className="inline mr-2" />
                      Item Code
                    </label>
                    <input
                      type="number"
                      value={newFoodItem.itemcode}
                      onChange={(e) =>
                        setNewFoodItem({
                          ...newFoodItem,
                          itemcode: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter item code number (e.g., 4)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Search items by this code number</p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Image</label>
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          setNewFoodItem({
                            ...newFoodItem,
                            image: e.target.files[0],
                          })
                        }
                        className="flex-1 p-3 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                      />

                      {newFoodItem.image && (
                        <div className="flex-shrink-0">
                          <p className="text-xs text-gray-500 mb-2">Preview:</p>
                          <img
                            src={URL.createObjectURL(newFoodItem.image)}
                            alt="Preview"
                            className="h-24 w-24 object-cover rounded-lg border"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Meal Type *</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {['Breakfast', 'Lunch', 'Dinner'].map((meal) => (
                        <label
                          key={meal}
                          className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={newFoodItem.mealTypes.includes(meal)}
                            onChange={(e) => {
                              const updatedMealTypes = e.target.checked
                                ? [...newFoodItem.mealTypes, meal]
                                : newFoodItem.mealTypes.filter((m) => m !== meal);
                              setNewFoodItem({
                                ...newFoodItem,
                                mealTypes: updatedMealTypes,
                              });
                            }}
                            className="h-5 w-5 text-black rounded border-gray-300 focus:ring-black"
                          />
                          <span className="ml-3 text-gray-700">{meal}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Food Type *</label>
                      <select
                        value={newFoodItem.foodType}
                        onChange={(e) =>
                          setNewFoodItem({
                            ...newFoodItem,
                            foodType: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      >
                        <option value="Veg">Veg</option>
                        <option value="Non-Veg">Non-Veg</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Status *</label>
                      <select
                        value={newFoodItem.status}
                        onChange={(e) =>
                          setNewFoodItem({
                            ...newFoodItem,
                            status: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      >
                        <option value="Available">Available</option>
                        <option value="Not Available">Not Available</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Available From</label>
                      <input
                        type="text"
                        value={
                          newFoodItem.availableFrom
                            ? `${newFoodItem.availableFrom.split(':')[0]}:${
                                newFoodItem.availableFrom.split(':')[1]
                              }`
                            : ''
                        }
                        onFocus={() => handleTimeFocus('availableFrom')}
                        readOnly
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent cursor-pointer"
                        placeholder="Select time (or leave empty for 24/7)"
                      />
                      {showTimePicker.availableFrom && (
                        <TimePickerModal
                          field="availableFrom"
                          onConfirm={(time) => confirmTimeSelection('availableFrom', time)}
                          onCancel={() => cancelTimeSelection('availableFrom')}
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Available Until</label>
                      <input
                        type="text"
                        value={
                          newFoodItem.availableUntil
                            ? `${newFoodItem.availableUntil.split(':')[0]}:${
                                newFoodItem.availableUntil.split(':')[1]
                              }`
                            : ''
                        }
                        onFocus={() => handleTimeFocus('availableUntil')}
                        readOnly
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent cursor-pointer"
                        placeholder="Select time (or leave empty for 24/7)"
                      />
                      {showTimePicker.availableUntil && (
                        <TimePickerModal
                          field="availableUntil"
                          onConfirm={(time) => confirmTimeSelection('availableUntil', time)}
                          onCancel={() => cancelTimeSelection('availableUntil')}
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Value</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newFoodItem.value || ''}
                        onChange={(e) =>
                          setNewFoodItem({
                            ...newFoodItem,
                            value: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="Optional value"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Value Type</label>
                      <select
                        value={newFoodItem.value_type || ''}
                        onChange={(e) =>
                          setNewFoodItem({
                            ...newFoodItem,
                            value_type: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select type</option>
                        <option value="P">Percentage</option>
                        <option value="F">Fixed</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Toggle Switches */}
              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-900">Show in Enhance Stay</span>
                    <p className="text-sm text-gray-500 mt-1">
                      Visible to customers during reservation
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newFoodItem.showEnhanceStay}
                      onChange={(e) =>
                        setNewFoodItem({
                          ...newFoodItem,
                          showEnhanceStay: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-900">Refundable</span>
                    <p className="text-sm text-gray-500 mt-1">Allow refunds for this item</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newFoodItem.refundable}
                      onChange={(e) =>
                        setNewFoodItem({
                          ...newFoodItem,
                          refundable: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-900">Show in Check-in</span>
                    <p className="text-sm text-gray-500 mt-1">Display during check-in process</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newFoodItem.showInCheckin || false}
                      onChange={(e) =>
                        setNewFoodItem({
                          ...newFoodItem,
                          showInCheckin: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-6 mt-6 border-t">
                <button
                  onClick={() => {
                    setShowAddPopup(false);
                    resetNewFoodItem();
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddFoodItem}
                  className="px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Add Food Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Food Item Popup */}
      {showEditPopup && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Edit Food Item</h2>
              <button
                onClick={() => setShowEditPopup(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Food Name *</label>
                    <input
                      type="text"
                      value={editingFoodItem.name}
                      onChange={(e) =>
                        setEditingFoodItem({
                          ...editingFoodItem,
                          name: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Description *</label>
                    <textarea
                      value={editingFoodItem.description}
                      onChange={(e) =>
                        setEditingFoodItem({
                          ...editingFoodItem,
                          description: e.target.value,
                        })
                      }
                      rows="3"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Price *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingFoodItem.price}
                      onChange={(e) =>
                        setEditingFoodItem({
                          ...editingFoodItem,
                          price: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Price Type</label>
                    <select
                      value={editingFoodItem.price_type}
                      onChange={(e) =>
                        setEditingFoodItem({
                          ...editingFoodItem,
                          price_type: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="Per item">Per Item</option>
                      <option value="Per person">Per Person</option>
                      <option value="Per plate">Per Plate</option>
                      <option value="Per pack">Per Pack</option>
                      <option value="Per day">Per Day</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Sub-Category *</label>
                    <select
                      value={editingFoodItem.subCategoryId}
                      onChange={(e) =>
                        setEditingFoodItem({
                          ...editingFoodItem,
                          subCategoryId: e.target.value,
                        })
                      }
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                        !editingFoodItem.subCategoryId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Sub-Category</option>
                      {subCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      <FaBarcode className="inline mr-2" />
                      Item Code
                    </label>
                    <input
                      type="number"
                      value={editingFoodItem.itemcode || ''}
                      onChange={(e) =>
                        setEditingFoodItem({
                          ...editingFoodItem,
                          itemcode: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter item code number"
                    />
                    <p className="text-xs text-gray-500 mt-1">Search items by this code number</p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Image</label>
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          setEditingFoodItem({
                            ...editingFoodItem,
                            image: e.target.files[0],
                          })
                        }
                        className="flex-1 p-3 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                      />

                      {editingFoodItem.image && (
                        <div className="flex-shrink-0">
                          <p className="text-xs text-gray-500 mb-2">Preview:</p>
                          <img
                            src={
                              typeof editingFoodItem.image === 'string'
                                ? editingFoodItem.image.startsWith('http')
                                  ? editingFoodItem.image
                                  : `${BQ_BASE_URL}/uploads/${editingFoodItem.image}`
                                : URL.createObjectURL(editingFoodItem.image)
                            }
                            alt="Preview"
                            className="h-24 w-24 object-cover rounded-lg border"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Meal Type *</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {['Breakfast', 'Lunch', 'Dinner'].map((meal) => (
                        <label
                          key={meal}
                          className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={editingFoodItem.mealTypes.includes(meal)}
                            onChange={(e) => {
                              const updatedMealTypes = e.target.checked
                                ? [...editingFoodItem.mealTypes, meal]
                                : editingFoodItem.mealTypes.filter((m) => m !== meal);
                              setEditingFoodItem({
                                ...editingFoodItem,
                                mealTypes: updatedMealTypes,
                              });
                            }}
                            className="h-5 w-5 text-black rounded border-gray-300 focus:ring-black"
                          />
                          <span className="ml-3 text-gray-700">{meal}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Food Type *</label>
                      <select
                        value={editingFoodItem.foodType}
                        onChange={(e) =>
                          setEditingFoodItem({
                            ...editingFoodItem,
                            foodType: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      >
                        <option value="Veg">Veg</option>
                        <option value="Non-Veg">Non-Veg</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Status *</label>
                      <select
                        value={editingFoodItem.status}
                        onChange={(e) =>
                          setEditingFoodItem({
                            ...editingFoodItem,
                            status: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      >
                        <option value="Available">Available</option>
                        <option value="Not Available">Not Available</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Available From</label>
                      <input
                        type="text"
                        value={
                          editingFoodItem.availableFrom
                            ? `${editingFoodItem.availableFrom.split(':')[0]}:${
                                editingFoodItem.availableFrom.split(':')[1]
                              }`
                            : ''
                        }
                        onFocus={() => handleTimeFocus('availableFrom')}
                        readOnly
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent cursor-pointer"
                        placeholder="Select time (or leave empty for 24/7)"
                      />
                      {showTimePicker.availableFrom && (
                        <TimePickerModal
                          field="availableFrom"
                          onConfirm={(time) => confirmTimeSelection('availableFrom', time)}
                          onCancel={() => cancelTimeSelection('availableFrom')}
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Available Until</label>
                      <input
                        type="text"
                        value={
                          editingFoodItem.availableUntil
                            ? `${editingFoodItem.availableUntil.split(':')[0]}:${
                                editingFoodItem.availableUntil.split(':')[1]
                              }`
                            : ''
                        }
                        onFocus={() => handleTimeFocus('availableUntil')}
                        readOnly
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent cursor-pointer"
                        placeholder="Select time (or leave empty for 24/7)"
                      />
                      {showTimePicker.availableUntil && (
                        <TimePickerModal
                          field="availableUntil"
                          onConfirm={(time) => confirmTimeSelection('availableUntil', time)}
                          onCancel={() => cancelTimeSelection('availableUntil')}
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Value</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editingFoodItem.value || ''}
                        onChange={(e) =>
                          setEditingFoodItem({
                            ...editingFoodItem,
                            value: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="Optional value"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Value Type</label>
                      <select
                        value={editingFoodItem.value_type || ''}
                        onChange={(e) =>
                          setEditingFoodItem({
                            ...editingFoodItem,
                            value_type: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select type</option>
                        <option value="P">Percentage</option>
                        <option value="F">Fixed</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Toggle Switches */}
              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-900">Show in Enhance Stay</span>
                    <p className="text-sm text-gray-500 mt-1">
                      Visible to customers during reservation
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingFoodItem.showEnhanceStay}
                      onChange={(e) =>
                        setEditingFoodItem({
                          ...editingFoodItem,
                          showEnhanceStay: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-900">Refundable</span>
                    <p className="text-sm text-gray-500 mt-1">Allow refunds for this item</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingFoodItem.refundable}
                      onChange={(e) =>
                        setEditingFoodItem({
                          ...editingFoodItem,
                          refundable: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-900">Show in Check-in</span>
                    <p className="text-sm text-gray-500 mt-1">Display during check-in process</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingFoodItem.showInCheckin || false}
                      onChange={(e) =>
                        setEditingFoodItem({
                          ...editingFoodItem,
                          showInCheckin: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-6 mt-6 border-t">
                <button
                  onClick={() => setShowEditPopup(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditFoodItem}
                  className="px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Update Food Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeletePopup && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTimesCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Delete Food Item</h2>
              <p className="text-gray-600">
                Are you sure you want to delete this food item? This action cannot be undone.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-center sm:justify-end">
              <button
                onClick={() => setShowDeletePopup(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteFoodItem}
                className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuItemsManagment;

// import React, { useState, useEffect } from 'react';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { useNavigate } from 'react-router-dom';
// import {
//   FaSearch,
//   FaTimes,
//   FaEdit,
//   FaTrash,
//   FaRupeeSign,
//   FaClock,
//   FaUtensils,
//   FaTag,
//   FaUndo,
//   FaCheckCircle,
//   FaTimesCircle,
//   FaHamburger,
//   FaFilter,
//   FaPlus,
//   FaBarcode,
//   FaLeaf,
//   FaDrumstickBite,
// } from 'react-icons/fa';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// function MenuItemsManagment() {
//   const navigate = useNavigate();
//   const [foodItems, setFoodItems] = useState([]);
//   const [showEditPopup, setShowEditPopup] = useState(false);
//   const [showDeletePopup, setShowDeletePopup] = useState(false);
//   const [showAddPopup, setShowAddPopup] = useState(false);
//   const [editingFoodItem, setEditingFoodItem] = useState(null);
//   const [deleteFoodId, setDeleteFoodId] = useState(null);
//   const [subCategories, setSubCategories] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [showTimePicker, setShowTimePicker] = useState({
//     availableFrom: false,
//     availableUntil: false,
//   });
//   const [tempTime, setTempTime] = useState({
//     availableFrom: '',
//     availableUntil: '',
//   });
//   const [isLoading, setIsLoading] = useState(true);
//   const [activeFilter, setActiveFilter] = useState('all');
//   const [showFilters, setShowFilters] = useState(false);

//   // ✅ New state for category and subcategory filtering
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [selectedSubcategory, setSelectedSubcategory] = useState(null);

//   const [newFoodItem, setNewFoodItem] = useState({
//     name: '',
//     description: '',
//     price: '',
//     price_type: 'Per person',
//     image: null,
//     foodType: 'Veg',
//     subCategoryId: '',
//     mealTypes: [],
//     availableFrom: '',
//     availableUntil: '',
//     status: 'Available',
//     showEnhanceStay: false,
//     refundable: false,
//     value: '',
//     value_type: '',
//     showInCheckin: false,
//     itemcode: '',
//   });

//   // Fetch food items, subcategories, and categories from API
//   useEffect(() => {
//     const fetchData = async () => {
//       setIsLoading(true);
//       try {
//         await fetchFoodItems();
//         await fetchSubCategories();
//         await fetchCategories(); // ✅ Fetch categories
//       } catch (error) {
//         toast.error('Failed to fetch data.');
//         console.error('Fetch error:', error);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   // Fetch food items with search and filter support
//   const fetchFoodItems = async (search = '', filter = '') => {
//     try {
//       let url = `${BQ_BASE_URL}/bq/api/items/`;

//       const params = new URLSearchParams();
//       if (search) {
//         if (!isNaN(search) && search.trim() !== '') {
//           params.append('itemcode', search);
//         } else {
//           params.append('search', search);
//         }
//       }
//       if (filter && filter !== 'all') {
//         const filterMap = {
//           available: 'isActive=true',
//           'not-available': 'isActive=false',
//           'enhance-stay': 'showEnhanceStay=true',
//           refundable: 'refundable=true',
//           veg: 'foodType=Veg',
//           'non-veg': 'foodType=Non-Veg',
//         };
//         if (filterMap[filter]) {
//           const [key, value] = filterMap[filter].split('=');
//           params.append(key, value);
//         }
//       }

//       const queryString = params.toString();
//       if (queryString) url += `?${queryString}`;

//       const response = await fetch(url, {
//         method: 'GET',
//         headers: {
//           Accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//       });

//       const data = await response.json();

//       if (response.ok) {
//         const mappedData = data.items.map((item) => ({
//           id: item.id,
//           name: item.title,
//           description: item.description,
//           price: item.price,
//           price_type: item.price_type || 'Per person',
//           image: item.image,
//           status: item.isActive ? 'Available' : 'Not Available',
//           foodType: item.foodType || 'Veg',
//           mealTypes: item.mealTypes || ['Breakfast'],
//           availableFrom: item.availableFrom || '',
//           availableUntil: item.availableUntil || '',
//           subCategoryId: item.subCategoryId || '',
//           showEnhanceStay: item.showEnhanceStay || false,
//           refundable: item.refundable || false,
//           value: item.value || 0,
//           value_type: item.value_type || '',
//           showInCheckin: item.showInCheckin || false,
//           itemcode: item.itemcode || 0,
//           createdDate: item.createdDate || '',
//           updatedDate: item.updatedDate || '',
//         }));
//         setFoodItems(mappedData);
//         return mappedData;
//       } else {
//         toast.error(data.message || 'Failed to fetch food items.');
//         return [];
//       }
//     } catch (error) {
//       console.error('Fetch error:', error);
//       toast.error('An error occurred while fetching food items.');
//       return [];
//     }
//   };

//   // ✅ Fetch categories
//   const fetchCategories = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/categories/`, {
//         method: 'GET',
//         headers: {
//           Accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//       });
//       const data = await response.json();

//       if (response.ok) {
//         setCategories(data);
//         return data;
//       } else {
//         console.error('Failed to fetch categories');
//         return [];
//       }
//     } catch (error) {
//       console.error('Fetch categories error:', error);
//       return [];
//     }
//   };

//   // Fetch subcategories
//   const fetchSubCategories = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/subcategories/`, {
//         method: 'GET',
//         headers: {
//           Accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//       });
//       const data = await response.json();

//       if (response.ok) {
//         setSubCategories(data);
//         return data;
//       } else {
//         toast.error(data.message || 'Failed to fetch subcategories.');
//         return [];
//       }
//     } catch (error) {
//       console.error('Fetch subcategories error:', error);
//       toast.error('An error occurred while fetching subcategories.');
//       return [];
//     }
//   };

//   // ✅ Get category ID from subcategory ID
//   const getCategoryIdFromSubCategoryId = (subCategoryId) => {
//     const subcategory = subCategories.find((sub) => sub.id === subCategoryId);
//     return subcategory ? subcategory.categoryid : null;
//   };

//   // ✅ Filter items based on selected category and subcategory
//   const getFilteredItems = () => {
//     let filtered = foodItems.filter((item) => {
//       const searchLower = searchQuery.toLowerCase();
//       const nameMatch = item.name.toLowerCase().includes(searchLower);
//       const itemcodeMatch =
//         !isNaN(searchQuery) && searchQuery.trim() !== ''
//           ? item.itemcode === parseInt(searchQuery)
//           : false;
//       return nameMatch || itemcodeMatch;
//     });

//     // ✅ Filter by category
//     if (selectedCategory) {
//       filtered = filtered.filter((item) => {
//         const categoryId = getCategoryIdFromSubCategoryId(item.subCategoryId);
//         return categoryId === selectedCategory;
//       });
//     }

//     // ✅ Filter by subcategory
//     if (selectedSubcategory) {
//       filtered = filtered.filter((item) => item.subCategoryId === selectedSubcategory);
//     }

//     // Apply status filters
//     switch (activeFilter) {
//       case 'available':
//         filtered = filtered.filter((item) => item.status === 'Available');
//         break;
//       case 'not-available':
//         filtered = filtered.filter((item) => item.status === 'Not Available');
//         break;
//       case 'enhance-stay':
//         filtered = filtered.filter((item) => item.showEnhanceStay);
//         break;
//       case 'refundable':
//         filtered = filtered.filter((item) => item.refundable);
//         break;
//       case 'veg':
//         filtered = filtered.filter((item) => item.foodType === 'Veg');
//         break;
//       case 'non-veg':
//         filtered = filtered.filter((item) => item.foodType === 'Non-Veg');
//         break;
//       default:
//         break;
//     }

//     return filtered;
//   };

//   const filteredFoodItems = getFilteredItems();

//   // ✅ Clear all filters including category and subcategory
//   const clearAllFilters = () => {
//     setSearchQuery('');
//     setActiveFilter('all');
//     setSelectedCategory(null);
//     setSelectedSubcategory(null);
//     fetchFoodItems('', 'all');
//   };

//   // Add new food item (POST)
//   const handleAddFoodItem = async () => {
//     if (
//       !newFoodItem.name ||
//       !newFoodItem.description ||
//       !newFoodItem.price ||
//       !newFoodItem.subCategoryId ||
//       !newFoodItem.mealTypes.length ||
//       !newFoodItem.foodType
//     ) {
//       toast.error('Please fill in all required fields.');
//       return;
//     }

//     const formatTime = (timeStr) => {
//       return timeStr && timeStr.length === 5 ? `${timeStr}:00` : timeStr || '00:00:00';
//     };

//     try {
//       const formData = new FormData();
//       formData.append('title', newFoodItem.name);
//       formData.append('description', newFoodItem.description);
//       formData.append('price', parseFloat(newFoodItem.price));
//       formData.append('price_type', newFoodItem.price_type);
//       formData.append('foodType', newFoodItem.foodType);
//       formData.append('subCategoryId', newFoodItem.subCategoryId);
//       formData.append('mealTypes', JSON.stringify(newFoodItem.mealTypes));
//       formData.append('availableFrom', formatTime(newFoodItem.availableFrom));
//       formData.append('availableUntil', formatTime(newFoodItem.availableUntil));
//       formData.append('isActive', newFoodItem.status === 'Available');
//       formData.append('showEnhanceStay', newFoodItem.showEnhanceStay);
//       formData.append('refundable', newFoodItem.refundable);
//       formData.append('value', newFoodItem.value || 0);
//       formData.append('value_type', newFoodItem.value_type || '');
//       formData.append('showInCheckin', newFoodItem.showInCheckin || false);
//       formData.append('itemcode', parseInt(newFoodItem.itemcode) || 0);

//       if (newFoodItem.image) {
//         formData.append('image', newFoodItem.image);
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/items/`, {
//         method: 'POST',
//         body: formData,
//       });

//       const data = await response.json();

//       if (response.ok) {
//         await fetchFoodItems(searchQuery, activeFilter);
//         setShowAddPopup(false);
//         resetNewFoodItem();
//         toast.success('Food item added successfully!');
//       } else {
//         toast.error(data.message || 'Failed to add food item.');
//       }
//     } catch (error) {
//       toast.error('An error occurred while adding the food item.');
//       console.error('Add error:', error);
//     }
//   };

//   // Edit food item (PUT)
//   const handleEditFoodItem = async () => {
//     if (
//       !editingFoodItem.name ||
//       !editingFoodItem.description ||
//       !editingFoodItem.price ||
//       !editingFoodItem.subCategoryId ||
//       !editingFoodItem.mealTypes.length ||
//       !editingFoodItem.foodType
//     ) {
//       toast.error('Please fill in all required fields.');
//       return;
//     }

//     const formatTime = (timeStr) => {
//       return timeStr && timeStr.length === 5 ? `${timeStr}:00` : timeStr || '00:00:00';
//     };

//     try {
//       const formData = new FormData();
//       formData.append('title', editingFoodItem.name);
//       formData.append('description', editingFoodItem.description);
//       formData.append('price', parseFloat(editingFoodItem.price));
//       formData.append('price_type', editingFoodItem.price_type);
//       formData.append('foodType', editingFoodItem.foodType);
//       formData.append('subCategoryId', editingFoodItem.subCategoryId);
//       formData.append('mealTypes', JSON.stringify(editingFoodItem.mealTypes));
//       formData.append('availableFrom', formatTime(editingFoodItem.availableFrom));
//       formData.append('availableUntil', formatTime(editingFoodItem.availableUntil));
//       formData.append('isActive', editingFoodItem.status === 'Available');
//       formData.append('showEnhanceStay', editingFoodItem.showEnhanceStay);
//       formData.append('refundable', editingFoodItem.refundable);
//       formData.append('value', editingFoodItem.value || 0);
//       formData.append('value_type', editingFoodItem.value_type || '');
//       formData.append('showInCheckin', editingFoodItem.showInCheckin || false);
//       formData.append('itemcode', parseInt(editingFoodItem.itemcode) || 0);

//       if (editingFoodItem.image) {
//         if (typeof editingFoodItem.image === 'string' && editingFoodItem.image.startsWith('http')) {
//           formData.append('image_url', editingFoodItem.image);
//         } else {
//           formData.append('image', editingFoodItem.image);
//         }
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/items/${editingFoodItem.id}`, {
//         method: 'PUT',
//         body: formData,
//       });

//       const data = await response.json();

//       if (response.ok) {
//         await fetchFoodItems(searchQuery, activeFilter);
//         setShowEditPopup(false);
//         toast.success('Food item updated successfully!');
//       } else {
//         toast.error(data.message || 'Failed to update food item.');
//       }
//     } catch (error) {
//       toast.error('An error occurred while updating the food item.');
//       console.error('Update error:', error);
//     }
//   };

//   // Delete food item (DELETE)
//   const handleDeleteFoodItem = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/items/${deleteFoodId}`, {
//         method: 'DELETE',
//         headers: {
//           Accept: 'application/json',
//         },
//       });

//       const data = await response.json();

//       if (response.ok) {
//         await fetchFoodItems(searchQuery, activeFilter);
//         setShowDeletePopup(false);
//         toast.success(data.message || 'Food item deleted successfully!');
//       } else {
//         toast.error(data.message || 'Failed to delete food item.');
//       }
//     } catch (error) {
//       toast.error('An error occurred while deleting the food item.');
//       console.error('Delete error:', error);
//     }
//   };

//   // Reset new food item form
//   const resetNewFoodItem = () => {
//     setNewFoodItem({
//       name: '',
//       description: '',
//       price: '',
//       price_type: 'Per person',
//       image: null,
//       foodType: 'Veg',
//       subCategoryId: '',
//       mealTypes: [],
//       availableFrom: '',
//       availableUntil: '',
//       status: 'Available',
//       showEnhanceStay: false,
//       refundable: false,
//       value: '',
//       value_type: '',
//       showInCheckin: false,
//       itemcode: '',
//     });
//   };

//   // Handle search with API
//   const handleSearch = async (query) => {
//     setSearchQuery(query);
//     await fetchFoodItems(query, activeFilter);
//   };

//   // Handle filter change with API
//   const handleFilterChange = async (filter) => {
//     setActiveFilter(filter);
//     await fetchFoodItems(searchQuery, filter);
//   };

//   // Handle time selection with custom picker
//   const handleTimeFocus = (field) => {
//     setShowTimePicker({
//       ...showTimePicker,
//       [field]: true,
//     });
//     setTempTime({
//       ...tempTime,
//       [field]: editingFoodItem ? editingFoodItem[field] : '12:00',
//     });
//   };

//   const handleTimeChange = (field, value) => {
//     setTempTime({
//       ...tempTime,
//       [field]: value,
//     });
//   };

//   const confirmTimeSelection = (field, time) => {
//     if (editingFoodItem) {
//       setEditingFoodItem({
//         ...editingFoodItem,
//         [field]: time,
//       });
//     } else {
//       setNewFoodItem({
//         ...newFoodItem,
//         [field]: time,
//       });
//     }
//     setShowTimePicker({
//       ...showTimePicker,
//       [field]: false,
//     });
//   };

//   const cancelTimeSelection = (field) => {
//     setShowTimePicker({
//       ...showTimePicker,
//       [field]: false,
//     });
//   };

//   // Custom time picker component
//   const TimePickerModal = ({ field, onConfirm, onCancel }) => {
//     const [selectedTime, setSelectedTime] = useState(tempTime[field] || '12:00');

//     const hours = Array.from({ length: 24 }, (_, i) => i);
//     const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

//     const formatTime = (hour, minute) => {
//       const period = hour >= 12 ? 'PM' : 'AM';
//       const displayHour = hour % 12 || 12;
//       return `${displayHour.toString().padStart(2, '0')}:${minute
//         .toString()
//         .padStart(2, '0')} ${period}`;
//     };

//     const handleHourChange = (hour) => {
//       const [_, minute] = selectedTime.split(':');
//       const newTime = `${hour}:${minute || '00'}`;
//       setSelectedTime(newTime);
//       handleTimeChange(field, newTime);
//     };

//     const handleMinuteChange = (minute) => {
//       const [hour] = selectedTime.split(':');
//       const newTime = `${hour}:${minute}`;
//       setSelectedTime(newTime);
//       handleTimeChange(field, newTime);
//     };

//     return (
//       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//         <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-sm sm:max-w-md relative">
//           <button
//             onClick={onCancel}
//             className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
//             aria-label="Close"
//           >
//             <FaTimes className="w-5 h-5" />
//           </button>

//           <h3 className="text-lg font-semibold mb-4 pr-6">
//             Select {field === 'availableFrom' ? 'Available From' : 'Available Until'} Time
//           </h3>

//           <div className="flex flex-wrap justify-between gap-4 mb-6">
//             <div className="flex-1 min-w-[100px]">
//               <label className="block text-sm font-medium mb-2">Hour</label>
//               <select
//                 value={selectedTime.split(':')[0]}
//                 onChange={(e) => handleHourChange(e.target.value)}
//                 className="p-2 border rounded w-full"
//               >
//                 {hours.map((hour) => (
//                   <option key={hour} value={hour}>
//                     {hour % 12 || 12}
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div className="flex-1 min-w-[100px]">
//               <label className="block text-sm font-medium mb-2">Minute</label>
//               <select
//                 value={selectedTime.split(':')[1]}
//                 onChange={(e) => handleMinuteChange(e.target.value)}
//                 className="p-2 border rounded w-full"
//               >
//                 {minutes.map((minute) => (
//                   <option key={minute} value={minute.toString().padStart(2, '0')}>
//                     {minute.toString().padStart(2, '0')}
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div className="flex-1 min-w-[100px]">
//               <label className="block text-sm font-medium mb-2">AM/PM</label>
//               <select
//                 value={parseInt(selectedTime.split(':')[0]) >= 12 ? 'PM' : 'AM'}
//                 onChange={(e) => {
//                   const [hour, minute] = selectedTime.split(':');
//                   const newHour =
//                     e.target.value === 'PM' ? (parseInt(hour) % 12) + 12 : parseInt(hour) % 12;
//                   handleHourChange(newHour);
//                 }}
//                 className="p-2 border rounded w-full"
//               >
//                 <option value="AM">AM</option>
//                 <option value="PM">PM</option>
//               </select>
//             </div>
//           </div>

//           <div className="text-center mb-4">
//             <div className="text-xl sm:text-2xl font-bold bg-gray-100 p-3 rounded">
//               {formatTime(
//                 parseInt(selectedTime.split(':')[0]),
//                 parseInt(selectedTime.split(':')[1])
//               )}
//             </div>
//           </div>

//           <div className="flex justify-end">
//             <button
//               onClick={() => onConfirm(selectedTime)}
//               className="w-full px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
//             >
//               OK
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   // Mobile filters dropdown
//   const FilterDropdown = () => (
//     <div className="relative">
//       <button
//         onClick={() => setShowFilters(!showFilters)}
//         className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
//       >
//         <FaFilter className="text-gray-600" />
//         <span className="hidden sm:inline">Filter</span>
//       </button>

//       {showFilters && (
//         <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
//           <div className="p-2">
//             {[
//               { value: 'all', label: 'All Items' },
//               { value: 'available', label: 'Available' },
//               { value: 'not-available', label: 'Not Available' },
//               { value: 'enhance-stay', label: 'Enhance Stay' },
//               { value: 'refundable', label: 'Refundable' },
//               { value: 'veg', label: 'Veg Only' },
//               { value: 'non-veg', label: 'Non-Veg Only' },
//             ].map((filter) => (
//               <button
//                 key={filter.value}
//                 onClick={() => {
//                   handleFilterChange(filter.value);
//                   setShowFilters(false);
//                 }}
//                 className={`w-full text-left px-3 py-2 rounded-md mb-1 last:mb-0 ${
//                   activeFilter === filter.value ? 'bg-black text-white' : 'hover:bg-gray-100'
//                 }`}
//               >
//                 {filter.label}
//               </button>
//             ))}
//           </div>
//         </div>
//       )}
//     </div>
//   );

//   // Loading skeleton
//   const LoadingSkeleton = () => (
//     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
//       {[...Array(8)].map((_, index) => (
//         <div
//           key={index}
//           className="border border-gray-200 rounded-lg overflow-hidden shadow animate-pulse"
//         >
//           <div className="h-48 bg-gray-300"></div>
//           <div className="p-4 space-y-3">
//             <div className="h-6 bg-gray-300 rounded"></div>
//             <div className="h-4 bg-gray-300 rounded"></div>
//             <div className="h-4 bg-gray-300 rounded"></div>
//             <div className="flex gap-2">
//               <div className="h-6 w-16 bg-gray-300 rounded"></div>
//               <div className="h-6 w-16 bg-gray-300 rounded"></div>
//             </div>
//           </div>
//         </div>
//       ))}
//     </div>
//   );

//   return (
//     <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
//       {/* Header Navigation */}
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Menu Management</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/mdm/menu-items-managment')}
//             className="btn text-black font-bold"
//           >
//             Menu Items Management
//           </button>
//           <button onClick={() => navigate('/dashboard/mdm/menu-Categorization')} className="btn ">
//             Menu Categorization
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/menu-pricing-promotions')}
//             className="btn "
//           >
//             Menu Pricing & Promotions
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/menu-analytics-insights')}
//             className="btn "
//           >
//             Menu Analytics & Insights
//           </button>
//         </div>
//       </header>

//       <h1 className="text-2xl font-bold text-center">Menu Management</h1>
//       <p className="text-sm text-center mb-6">
//         Menu Management allows admins to create food categories, subcategories, and individual food
//         items for organized menu structuring and updates.
//       </p>

//       {/* Search and Action Buttons */}
//       <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-2">
//         <div className="relative flex-1 w-full">
//           <input
//             type="text"
//             placeholder="Search by name or item code (e.g., 'mango' or '4')..."
//             value={searchQuery}
//             onChange={(e) => handleSearch(e.target.value)}
//             className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm outline-none"
//           />
//           <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//         </div>

//         <div className="flex flex-wrap gap-3 w-full sm:w-auto">
//           <FilterDropdown />

//           {/* Desktop Filter Buttons */}
//           <div className="hidden sm:flex gap-2">
//             {[
//               { value: 'all', label: 'All' },
//               { value: 'available', label: 'Available' },
//               { value: 'refundable', label: 'Refundable' },
//               { value: 'enhance-stay', label: 'Enhance Stay' },
//             ].map((filter) => (
//               <button
//                 key={filter.value}
//                 onClick={() => handleFilterChange(filter.value)}
//                 className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
//                   activeFilter === filter.value
//                     ? 'bg-black text-white'
//                     : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
//                 }`}
//               >
//                 {filter.label}
//               </button>
//             ))}
//           </div>

//           {/* ✅ Clear All Filters Button */}
//           {(selectedCategory || selectedSubcategory || searchQuery || activeFilter !== 'all') && (
//             <button
//               onClick={clearAllFilters}
//               className="px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 text-sm font-medium"
//             >
//               <FaTimes size={12} />
//               Clear Filters
//             </button>
//           )}

//           <button
//             onClick={() => {
//               resetNewFoodItem();
//               setShowAddPopup(true);
//             }}
//             className="flex-1 sm:flex-none px-4 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
//           >
//             <FaPlus />
//             Add Food Item
//           </button>
//         </div>
//       </div>

//       {/* Food Items Count */}
//       <div className="mb-2 flex justify-between items-center">
//         <div>
//           <span className="text-gray-600">
//             Showing {filteredFoodItems.length} of {foodItems.length} menu items
//           </span>
//           {selectedCategory && (
//             <span className="ml-2 text-sm text-gray-500">
//               in {categories.find((c) => c.id === selectedCategory)?.name}
//             </span>
//           )}
//           {selectedSubcategory && (
//             <span className="ml-2 text-sm text-gray-500">
//               › {subCategories.find((s) => s.id === selectedSubcategory)?.name}
//             </span>
//           )}
//         </div>
//         <div className="text-sm text-gray-500">
//           {activeFilter !== 'all' && (
//             <button
//               onClick={() => handleFilterChange('all')}
//               className="text-blue-600 hover:underline"
//             >
//               Clear status filter
//             </button>
//           )}
//         </div>
//       </div>

//       {/* ✅ Category Filters - Like FoodOrder */}
//       <div className="overflow-x-auto mb-3 pb-2">
//         <div className="flex gap-2 min-w-min">
//           <button
//             onClick={() => {
//               setSelectedCategory(null);
//               setSelectedSubcategory(null);
//             }}
//             className={`px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
//               selectedCategory === null
//                 ? 'bg-black text-white'
//                 : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
//             }`}
//           >
//             All Categories
//           </button>
//           {categories.map((category) => (
//             <button
//               key={category.id}
//               onClick={() => {
//                 setSelectedCategory(category.id);
//                 setSelectedSubcategory(null);
//               }}
//               className={`px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
//                 selectedCategory === category.id
//                   ? 'bg-black text-white'
//                   : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
//               }`}
//             >
//               {category.name}
//             </button>
//           ))}
//         </div>
//       </div>

//       {/* ✅ Subcategory Filters - Like FoodOrder */}
//       {selectedCategory &&
//         subCategories.filter((s) => s.categoryid === selectedCategory).length > 0 && (
//           <div className="overflow-x-auto mb-6 pb-2">
//             <div className="flex gap-2 min-w-min">
//               <button
//                 onClick={() => setSelectedSubcategory(null)}
//                 className={`px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
//                   selectedSubcategory === null
//                     ? 'bg-black text-white'
//                     : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
//                 }`}
//               >
//                 All Subcategories
//               </button>
//               {subCategories
//                 .filter((sub) => sub.categoryid === selectedCategory)
//                 .map((sub) => (
//                   <button
//                     key={sub.id}
//                     onClick={() => setSelectedSubcategory(sub.id)}
//                     className={`px-4 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
//                       selectedSubcategory === sub.id
//                         ? 'bg-black text-white'
//                         : 'bg-white text-gray-500 border border-gray-200 hover:border-black'
//                     }`}
//                   >
//                     {sub.name}
//                   </button>
//                 ))}
//             </div>
//           </div>
//         )}

//       {/* Food Cards Grid */}
//       {isLoading ? (
//         <LoadingSkeleton />
//       ) : filteredFoodItems.length === 0 ? (
//         <div className="text-center py-12">
//           <div className="text-gray-400 text-6xl mb-4">
//             <FaUtensils className="mx-auto" />
//           </div>
//           <h3 className="text-xl font-semibold text-gray-700 mb-2">No food items found</h3>
//           <p className="text-gray-500 mb-6">
//             {searchQuery || selectedCategory || selectedSubcategory
//               ? 'Try adjusting your filters or search terms'
//               : 'Start by adding your first food item'}
//           </p>
//           {!searchQuery && !selectedCategory && !selectedSubcategory && (
//             <button
//               onClick={() => {
//                 resetNewFoodItem();
//                 setShowAddPopup(true);
//               }}
//               className="px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
//             >
//               Add First Food Item
//             </button>
//           )}
//         </div>
//       ) : (
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
//           {filteredFoodItems.map((item) => {
//             // ✅ Get category name for display
//             const subCategory = subCategories.find((s) => s.id === item.subCategoryId);
//             const category = subCategory
//               ? categories.find((c) => c.id === subCategory.categoryid)
//               : null;

//             return (
//               <div
//                 key={item.id}
//                 className="group border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
//               >
//                 {/* Image with badges */}
//                 <div className="relative h-48 sm:h-56 overflow-hidden">
//                   <img
//                     src={item.image || 'https://via.placeholder.com/400x300?text=No+Image'}
//                     alt={item.name}
//                     className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
//                     onError={(e) => {
//                       e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
//                     }}
//                   />

//                   {/* Top badges */}
//                   <div className="absolute top-3 right-3 flex flex-col gap-2">
//                     <span
//                       className={`text-xs font-semibold px-2 py-1 rounded-full ${
//                         item.status === 'Available'
//                           ? 'bg-green-100 text-green-800'
//                           : 'bg-red-100 text-red-800'
//                       }`}
//                     >
//                       {item.status}
//                     </span>

//                     {item.refundable && (
//                       <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
//                         <FaUndo className="text-xs" />
//                         Refundable
//                       </span>
//                     )}
//                   </div>

//                   {/* Bottom badges */}
//                   <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
//                     {item.showEnhanceStay && (
//                       <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
//                         <FaTag />
//                         Enhance Stay
//                       </span>
//                     )}

//                     <span
//                       className={`text-xs font-semibold px-2 py-1 rounded-full ${
//                         item.foodType === 'Veg'
//                           ? 'bg-green-100 text-green-800'
//                           : 'bg-red-100 text-red-800'
//                       }`}
//                     >
//                       {item.foodType === 'Veg' ? (
//                         <FaLeaf className="inline mr-1" />
//                       ) : (
//                         <FaDrumstickBite className="inline mr-1" />
//                       )}
//                       {item.foodType}
//                     </span>

//                     {item.itemcode > 0 && (
//                       <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
//                         <FaBarcode className="text-xs" />
//                         Code: {item.itemcode}
//                       </span>
//                     )}
//                   </div>
//                 </div>

//                 {/* Content */}
//                 <div className="p-4 flex-grow flex flex-col">
//                   {/* Name and Price */}
//                   <div className="mb-3">
//                     <h2 className="text-lg font-bold text-gray-900 line-clamp-1 mb-1">
//                       {item.name}
//                     </h2>
//                     <div className="flex items-center ">
//                       <div className="flex items-center gap-2">
//                         <FaRupeeSign className="text-gray-700" />
//                         <span className="text-xl font-bold text-gray-900">{item.price} </span>
//                       </div>
//                       <span className="text-sm text-gray-600">/{item.price_type}</span>
//                     </div>
//                     {/* ✅ Show Category/Subcategory */}
//                     {category && (
//                       <div className="text-xs text-gray-500 mt-1">
//                         {category.name} {subCategory && `› ${subCategory.name}`}
//                       </div>
//                     )}
//                   </div>

//                   {/* Description */}
//                   <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-grow">
//                     {item.description}
//                   </p>

//                   {/* Meal Types */}
//                   <div className="mb-4">
//                     <div className="flex flex-wrap gap-2">
//                       {item.mealTypes.map((type) => (
//                         <span
//                           key={type}
//                           className="bg-gray-100 text-gray-800 text-xs py-1 px-2 rounded flex items-center gap-1"
//                         >
//                           <FaUtensils className="text-xs" />
//                           {type}
//                         </span>
//                       ))}
//                     </div>
//                   </div>

//                   {/* Availability Times */}
//                   {(item.availableFrom || item.availableUntil) && (
//                     <div className="mb-4 p-2 bg-gray-50 rounded-lg">
//                       <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
//                         <FaClock />
//                         <span className="font-medium">Available</span>
//                       </div>
//                       <div className="flex items-center justify-between text-xs">
//                         {item.availableFrom ? (
//                           <span className="text-gray-700">
//                             From: {item.availableFrom.substring(0, 5)}
//                           </span>
//                         ) : (
//                           <span className="text-gray-400">No start time</span>
//                         )}
//                         {item.availableUntil ? (
//                           <span className="text-gray-700">
//                             Until: {item.availableUntil.substring(0, 5)}
//                           </span>
//                         ) : (
//                           <span className="text-gray-400">No end time</span>
//                         )}
//                       </div>
//                     </div>
//                   )}

//                   {/* Additional Info */}
//                   <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
//                     {item.showInCheckin && (
//                       <span className="flex items-center gap-1">
//                         <FaCheckCircle className="text-green-500" />
//                         Shows in Check-in
//                       </span>
//                     )}
//                     {item.value > 0 && <span>Tax : {item.value}%</span>}
//                   </div>

//                   {/* Action Buttons */}
//                   <div className="flex gap-2 pt-3 border-t border-gray-100">
//                     <button
//                       onClick={() => {
//                         setEditingFoodItem({
//                           ...item,
//                           subCategoryId: item.subCategoryId || '',
//                           image: item.image || '',
//                         });
//                         setShowEditPopup(true);
//                       }}
//                       className="flex-1 px-3 py-2 bg-gray-900 hover:bg-black text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
//                     >
//                       <FaEdit />
//                       <span className="hidden xs:inline">Edit</span>
//                     </button>

//                     <button
//                       onClick={() => {
//                         setDeleteFoodId(item.id);
//                         setShowDeletePopup(true);
//                       }}
//                       className="flex-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
//                     >
//                       <FaTrash />
//                       <span className="hidden xs:inline">Delete</span>
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             );
//           })}
//         </div>
//       )}

//       {/* Toast Notifications */}
//       <ToastContainer
//         position="bottom-right"
//         autoClose={3000}
//         hideProgressBar={false}
//         newestOnTop
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//       />

//       {/* Add Food Item Popup */}
//       {showAddPopup && (
//         <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
//           <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
//             <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
//               <h2 className="text-xl font-semibold">Add New Food Item</h2>
//               <button
//                 onClick={() => setShowAddPopup(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 <FaTimes className="w-5 h-5" />
//               </button>
//             </div>

//             <div className="p-6">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                 {/* Left Column */}
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Food Name *</label>
//                     <input
//                       type="text"
//                       value={newFoodItem.name}
//                       onChange={(e) =>
//                         setNewFoodItem({
//                           ...newFoodItem,
//                           name: e.target.value,
//                         })
//                       }
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                       placeholder="Enter food name"
//                     />
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Description *</label>
//                     <textarea
//                       value={newFoodItem.description}
//                       onChange={(e) =>
//                         setNewFoodItem({
//                           ...newFoodItem,
//                           description: e.target.value,
//                         })
//                       }
//                       rows="3"
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                       placeholder="Enter description"
//                     />
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Price *</label>
//                     <input
//                       type="number"
//                       step="0.01"
//                       value={newFoodItem.price}
//                       onChange={(e) =>
//                         setNewFoodItem({
//                           ...newFoodItem,
//                           price: e.target.value,
//                         })
//                       }
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                       placeholder="Enter price"
//                     />
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Price Type</label>
//                     <select
//                       value={newFoodItem.price_type}
//                       onChange={(e) =>
//                         setNewFoodItem({
//                           ...newFoodItem,
//                           price_type: e.target.value,
//                         })
//                       }
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                     >
//                       <option value="Per item">Per Item</option>
//                       <option value="Per person">Per Person</option>
//                       <option value="Per plate">Per Plate</option>
//                       <option value="Per pack">Per Pack</option>
//                       <option value="Per day">Per Day</option>
//                     </select>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Sub-Category *</label>
//                     <select
//                       value={newFoodItem.subCategoryId}
//                       onChange={(e) =>
//                         setNewFoodItem({
//                           ...newFoodItem,
//                           subCategoryId: e.target.value,
//                         })
//                       }
//                       className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
//                         !newFoodItem.subCategoryId ? 'border-red-500' : 'border-gray-300'
//                       }`}
//                     >
//                       <option value="">Select Sub-Category</option>
//                       {subCategories.map((cat) => (
//                         <option key={cat.id} value={cat.id}>
//                           {cat.name}
//                         </option>
//                       ))}
//                     </select>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">
//                       <FaBarcode className="inline mr-2" />
//                       Item Code
//                     </label>
//                     <input
//                       type="number"
//                       value={newFoodItem.itemcode}
//                       onChange={(e) =>
//                         setNewFoodItem({
//                           ...newFoodItem,
//                           itemcode: e.target.value,
//                         })
//                       }
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                       placeholder="Enter item code number (e.g., 4)"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">Search items by this code number</p>
//                   </div>
//                 </div>

//                 {/* Right Column */}
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Image</label>
//                     <div className="flex flex-col sm:flex-row gap-4 items-start">
//                       <input
//                         type="file"
//                         accept="image/*"
//                         onChange={(e) =>
//                           setNewFoodItem({
//                             ...newFoodItem,
//                             image: e.target.files[0],
//                           })
//                         }
//                         className="flex-1 p-3 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
//                       />

//                       {newFoodItem.image && (
//                         <div className="flex-shrink-0">
//                           <p className="text-xs text-gray-500 mb-2">Preview:</p>
//                           <img
//                             src={URL.createObjectURL(newFoodItem.image)}
//                             alt="Preview"
//                             className="h-24 w-24 object-cover rounded-lg border"
//                           />
//                         </div>
//                       )}
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Meal Type *</label>
//                     <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
//                       {['Breakfast', 'Lunch', 'Dinner'].map((meal) => (
//                         <label
//                           key={meal}
//                           className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
//                         >
//                           <input
//                             type="checkbox"
//                             checked={newFoodItem.mealTypes.includes(meal)}
//                             onChange={(e) => {
//                               const updatedMealTypes = e.target.checked
//                                 ? [...newFoodItem.mealTypes, meal]
//                                 : newFoodItem.mealTypes.filter((m) => m !== meal);
//                               setNewFoodItem({
//                                 ...newFoodItem,
//                                 mealTypes: updatedMealTypes,
//                               });
//                             }}
//                             className="h-5 w-5 text-black rounded border-gray-300 focus:ring-black"
//                           />
//                           <span className="ml-3 text-gray-700">{meal}</span>
//                         </label>
//                       ))}
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Food Type *</label>
//                       <select
//                         value={newFoodItem.foodType}
//                         onChange={(e) =>
//                           setNewFoodItem({
//                             ...newFoodItem,
//                             foodType: e.target.value,
//                           })
//                         }
//                         className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                       >
//                         <option value="Veg">Veg</option>
//                         <option value="Non-Veg">Non-Veg</option>
//                       </select>
//                     </div>

//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Status *</label>
//                       <select
//                         value={newFoodItem.status}
//                         onChange={(e) =>
//                           setNewFoodItem({
//                             ...newFoodItem,
//                             status: e.target.value,
//                           })
//                         }
//                         className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                       >
//                         <option value="Available">Available</option>
//                         <option value="Not Available">Not Available</option>
//                       </select>
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Available From</label>
//                       <input
//                         type="text"
//                         value={
//                           newFoodItem.availableFrom
//                             ? `${newFoodItem.availableFrom.split(':')[0]}:${
//                                 newFoodItem.availableFrom.split(':')[1]
//                               }`
//                             : ''
//                         }
//                         onFocus={() => handleTimeFocus('availableFrom')}
//                         readOnly
//                         className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent cursor-pointer"
//                         placeholder="Select time"
//                       />
//                       {showTimePicker.availableFrom && (
//                         <TimePickerModal
//                           field="availableFrom"
//                           onConfirm={(time) => confirmTimeSelection('availableFrom', time)}
//                           onCancel={() => cancelTimeSelection('availableFrom')}
//                         />
//                       )}
//                     </div>

//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Available Until</label>
//                       <input
//                         type="text"
//                         value={
//                           newFoodItem.availableUntil
//                             ? `${newFoodItem.availableUntil.split(':')[0]}:${
//                                 newFoodItem.availableUntil.split(':')[1]
//                               }`
//                             : ''
//                         }
//                         onFocus={() => handleTimeFocus('availableUntil')}
//                         readOnly
//                         className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent cursor-pointer"
//                         placeholder="Select time"
//                       />
//                       {showTimePicker.availableUntil && (
//                         <TimePickerModal
//                           field="availableUntil"
//                           onConfirm={(time) => confirmTimeSelection('availableUntil', time)}
//                           onCancel={() => cancelTimeSelection('availableUntil')}
//                         />
//                       )}
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Value</label>
//                       <input
//                         type="number"
//                         step="0.01"
//                         value={newFoodItem.value || ''}
//                         onChange={(e) =>
//                           setNewFoodItem({
//                             ...newFoodItem,
//                             value: e.target.value,
//                           })
//                         }
//                         className="w-full p-3 border border-gray-300 rounded-lg"
//                         placeholder="Optional value"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Value Type</label>
//                       <select
//                         value={newFoodItem.value_type || ''}
//                         onChange={(e) =>
//                           setNewFoodItem({
//                             ...newFoodItem,
//                             value_type: e.target.value,
//                           })
//                         }
//                         className="w-full p-3 border border-gray-300 rounded-lg"
//                       >
//                         <option value="">Select type</option>
//                         <option value="P">Percentage</option>
//                         <option value="F">Fixed</option>
//                       </select>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Toggle Switches */}
//               <div className="mt-8 space-y-4">
//                 <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
//                   <div>
//                     <span className="font-medium text-gray-900">Show in Enhance Stay</span>
//                     <p className="text-sm text-gray-500 mt-1">
//                       Visible to customers during reservation
//                     </p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       checked={newFoodItem.showEnhanceStay}
//                       onChange={(e) =>
//                         setNewFoodItem({
//                           ...newFoodItem,
//                           showEnhanceStay: e.target.checked,
//                         })
//                       }
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
//                   </label>
//                 </div>

//                 <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
//                   <div>
//                     <span className="font-medium text-gray-900">Refundable</span>
//                     <p className="text-sm text-gray-500 mt-1">Allow refunds for this item</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       checked={newFoodItem.refundable}
//                       onChange={(e) =>
//                         setNewFoodItem({
//                           ...newFoodItem,
//                           refundable: e.target.checked,
//                         })
//                       }
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
//                   </label>
//                 </div>

//                 <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
//                   <div>
//                     <span className="font-medium text-gray-900">Show in Check-in</span>
//                     <p className="text-sm text-gray-500 mt-1">Display during check-in process</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       checked={newFoodItem.showInCheckin || false}
//                       onChange={(e) =>
//                         setNewFoodItem({
//                           ...newFoodItem,
//                           showInCheckin: e.target.checked,
//                         })
//                       }
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
//                   </label>
//                 </div>
//               </div>

//               {/* Action Buttons */}
//               <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-6 mt-6 border-t">
//                 <button
//                   onClick={() => {
//                     setShowAddPopup(false);
//                     resetNewFoodItem();
//                   }}
//                   className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={handleAddFoodItem}
//                   className="px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
//                 >
//                   Add Food Item
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Edit Food Item Popup */}
//       {showEditPopup && (
//         <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
//           <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
//             <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
//               <h2 className="text-xl font-semibold">Edit Food Item</h2>
//               <button
//                 onClick={() => setShowEditPopup(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 <FaTimes className="w-5 h-5" />
//               </button>
//             </div>

//             <div className="p-6">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                 {/* Left Column */}
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Food Name *</label>
//                     <input
//                       type="text"
//                       value={editingFoodItem.name}
//                       onChange={(e) =>
//                         setEditingFoodItem({
//                           ...editingFoodItem,
//                           name: e.target.value,
//                         })
//                       }
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                     />
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Description *</label>
//                     <textarea
//                       value={editingFoodItem.description}
//                       onChange={(e) =>
//                         setEditingFoodItem({
//                           ...editingFoodItem,
//                           description: e.target.value,
//                         })
//                       }
//                       rows="3"
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                     />
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Price *</label>
//                     <input
//                       type="number"
//                       step="0.01"
//                       value={editingFoodItem.price}
//                       onChange={(e) =>
//                         setEditingFoodItem({
//                           ...editingFoodItem,
//                           price: e.target.value,
//                         })
//                       }
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                     />
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Price Type</label>
//                     <select
//                       value={editingFoodItem.price_type}
//                       onChange={(e) =>
//                         setEditingFoodItem({
//                           ...editingFoodItem,
//                           price_type: e.target.value,
//                         })
//                       }
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                     >
//                       <option value="Per item">Per Item</option>
//                       <option value="Per person">Per Person</option>
//                       <option value="Per plate">Per Plate</option>
//                       <option value="Per pack">Per Pack</option>
//                       <option value="Per day">Per Day</option>
//                     </select>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Sub-Category *</label>
//                     <select
//                       value={editingFoodItem.subCategoryId}
//                       onChange={(e) =>
//                         setEditingFoodItem({
//                           ...editingFoodItem,
//                           subCategoryId: e.target.value,
//                         })
//                       }
//                       className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
//                         !editingFoodItem.subCategoryId ? 'border-red-500' : 'border-gray-300'
//                       }`}
//                     >
//                       <option value="">Select Sub-Category</option>
//                       {subCategories.map((cat) => (
//                         <option key={cat.id} value={cat.id}>
//                           {cat.name}
//                         </option>
//                       ))}
//                     </select>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">
//                       <FaBarcode className="inline mr-2" />
//                       Item Code
//                     </label>
//                     <input
//                       type="number"
//                       value={editingFoodItem.itemcode || ''}
//                       onChange={(e) =>
//                         setEditingFoodItem({
//                           ...editingFoodItem,
//                           itemcode: e.target.value,
//                         })
//                       }
//                       className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                       placeholder="Enter item code number"
//                     />
//                     <p className="text-xs text-gray-500 mt-1">Search items by this code number</p>
//                   </div>
//                 </div>

//                 {/* Right Column */}
//                 <div className="space-y-4">
//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Image</label>
//                     <div className="flex flex-col sm:flex-row gap-4 items-start">
//                       <input
//                         type="file"
//                         accept="image/*"
//                         onChange={(e) =>
//                           setEditingFoodItem({
//                             ...editingFoodItem,
//                             image: e.target.files[0],
//                           })
//                         }
//                         className="flex-1 p-3 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
//                       />

//                       {editingFoodItem.image && (
//                         <div className="flex-shrink-0">
//                           <p className="text-xs text-gray-500 mb-2">Preview:</p>
//                           <img
//                             src={
//                               typeof editingFoodItem.image === 'string'
//                                 ? editingFoodItem.image.startsWith('http')
//                                   ? editingFoodItem.image
//                                   : `${BQ_BASE_URL}/uploads/${editingFoodItem.image}`
//                                 : URL.createObjectURL(editingFoodItem.image)
//                             }
//                             alt="Preview"
//                             className="h-24 w-24 object-cover rounded-lg border"
//                           />
//                         </div>
//                       )}
//                     </div>
//                   </div>

//                   <div>
//                     <label className="block text-sm font-semibold mb-2">Meal Type *</label>
//                     <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
//                       {['Breakfast', 'Lunch', 'Dinner'].map((meal) => (
//                         <label
//                           key={meal}
//                           className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
//                         >
//                           <input
//                             type="checkbox"
//                             checked={editingFoodItem.mealTypes.includes(meal)}
//                             onChange={(e) => {
//                               const updatedMealTypes = e.target.checked
//                                 ? [...editingFoodItem.mealTypes, meal]
//                                 : editingFoodItem.mealTypes.filter((m) => m !== meal);
//                               setEditingFoodItem({
//                                 ...editingFoodItem,
//                                 mealTypes: updatedMealTypes,
//                               });
//                             }}
//                             className="h-5 w-5 text-black rounded border-gray-300 focus:ring-black"
//                           />
//                           <span className="ml-3 text-gray-700">{meal}</span>
//                         </label>
//                       ))}
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Food Type *</label>
//                       <select
//                         value={editingFoodItem.foodType}
//                         onChange={(e) =>
//                           setEditingFoodItem({
//                             ...editingFoodItem,
//                             foodType: e.target.value,
//                           })
//                         }
//                         className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                       >
//                         <option value="Veg">Veg</option>
//                         <option value="Non-Veg">Non-Veg</option>
//                       </select>
//                     </div>

//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Status *</label>
//                       <select
//                         value={editingFoodItem.status}
//                         onChange={(e) =>
//                           setEditingFoodItem({
//                             ...editingFoodItem,
//                             status: e.target.value,
//                           })
//                         }
//                         className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
//                       >
//                         <option value="Available">Available</option>
//                         <option value="Not Available">Not Available</option>
//                       </select>
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Available From</label>
//                       <input
//                         type="text"
//                         value={
//                           editingFoodItem.availableFrom
//                             ? `${editingFoodItem.availableFrom.split(':')[0]}:${
//                                 editingFoodItem.availableFrom.split(':')[1]
//                               }`
//                             : ''
//                         }
//                         onFocus={() => handleTimeFocus('availableFrom')}
//                         readOnly
//                         className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent cursor-pointer"
//                         placeholder="Select time"
//                       />
//                       {showTimePicker.availableFrom && (
//                         <TimePickerModal
//                           field="availableFrom"
//                           onConfirm={(time) => confirmTimeSelection('availableFrom', time)}
//                           onCancel={() => cancelTimeSelection('availableFrom')}
//                         />
//                       )}
//                     </div>

//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Available Until</label>
//                       <input
//                         type="text"
//                         value={
//                           editingFoodItem.availableUntil
//                             ? `${editingFoodItem.availableUntil.split(':')[0]}:${
//                                 editingFoodItem.availableUntil.split(':')[1]
//                               }`
//                             : ''
//                         }
//                         onFocus={() => handleTimeFocus('availableUntil')}
//                         readOnly
//                         className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent cursor-pointer"
//                         placeholder="Select time"
//                       />
//                       {showTimePicker.availableUntil && (
//                         <TimePickerModal
//                           field="availableUntil"
//                           onConfirm={(time) => confirmTimeSelection('availableUntil', time)}
//                           onCancel={() => cancelTimeSelection('availableUntil')}
//                         />
//                       )}
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Value</label>
//                       <input
//                         type="number"
//                         step="0.01"
//                         value={editingFoodItem.value || ''}
//                         onChange={(e) =>
//                           setEditingFoodItem({
//                             ...editingFoodItem,
//                             value: e.target.value,
//                           })
//                         }
//                         className="w-full p-3 border border-gray-300 rounded-lg"
//                         placeholder="Optional value"
//                       />
//                     </div>

//                     <div>
//                       <label className="block text-sm font-semibold mb-2">Value Type</label>
//                       <select
//                         value={editingFoodItem.value_type || ''}
//                         onChange={(e) =>
//                           setEditingFoodItem({
//                             ...editingFoodItem,
//                             value_type: e.target.value,
//                           })
//                         }
//                         className="w-full p-3 border border-gray-300 rounded-lg"
//                       >
//                         <option value="">Select type</option>
//                         <option value="P">Percentage</option>
//                         <option value="F">Fixed</option>
//                       </select>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Toggle Switches */}
//               <div className="mt-8 space-y-4">
//                 <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
//                   <div>
//                     <span className="font-medium text-gray-900">Show in Enhance Stay</span>
//                     <p className="text-sm text-gray-500 mt-1">
//                       Visible to customers during reservation
//                     </p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       checked={editingFoodItem.showEnhanceStay}
//                       onChange={(e) =>
//                         setEditingFoodItem({
//                           ...editingFoodItem,
//                           showEnhanceStay: e.target.checked,
//                         })
//                       }
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
//                   </label>
//                 </div>

//                 <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
//                   <div>
//                     <span className="font-medium text-gray-900">Refundable</span>
//                     <p className="text-sm text-gray-500 mt-1">Allow refunds for this item</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       checked={editingFoodItem.refundable}
//                       onChange={(e) =>
//                         setEditingFoodItem({
//                           ...editingFoodItem,
//                           refundable: e.target.checked,
//                         })
//                       }
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
//                   </label>
//                 </div>

//                 <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
//                   <div>
//                     <span className="font-medium text-gray-900">Show in Check-in</span>
//                     <p className="text-sm text-gray-500 mt-1">Display during check-in process</p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer">
//                     <input
//                       type="checkbox"
//                       checked={editingFoodItem.showInCheckin || false}
//                       onChange={(e) =>
//                         setEditingFoodItem({
//                           ...editingFoodItem,
//                           showInCheckin: e.target.checked,
//                         })
//                       }
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
//                   </label>
//                 </div>
//               </div>

//               {/* Action Buttons */}
//               <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-6 mt-6 border-t">
//                 <button
//                   onClick={() => setShowEditPopup(false)}
//                   className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={handleEditFoodItem}
//                   className="px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
//                 >
//                   Update Food Item
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Delete Confirmation Popup */}
//       {showDeletePopup && (
//         <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4">
//           <div className="bg-white rounded-xl w-full max-w-md p-6">
//             <div className="text-center mb-6">
//               <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
//                 <FaTimesCircle className="w-8 h-8 text-red-600" />
//               </div>
//               <h2 className="text-xl font-semibold mb-2">Delete Food Item</h2>
//               <p className="text-gray-600">
//                 Are you sure you want to delete this food item? This action cannot be undone.
//               </p>
//             </div>

//             <div className="flex flex-col-reverse sm:flex-row gap-3 justify-center sm:justify-end">
//               <button
//                 onClick={() => setShowDeletePopup(false)}
//                 className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleDeleteFoodItem}
//                 className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
//               >
//                 Delete Item
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default MenuItemsManagment;
